import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { limitFragment } from '@/fragments/Limits';
import { LimitTypeEnum } from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  CancelPlayerGlobalLossLimitMutation,
  CancelPlayerGlobalLossLimitMutationVariables,
} from './__generated__/PlayerCancelGlobalLossLimitForm';

const query = graphql`
  query SanityPlayerCancelGlobalLossLimitForm {
    sanityPlayerCancelGlobalLossLimitsForm {
      title {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const cancelPendingGlobalLossLimitMutation = gql`
  mutation CancelPlayerGlobalLossLimit(
    $playerId: ID!
    $limitPeriodType: LimitTypeEnum!
  ) {
    cancelPendingGlobalLossLimitByPlayerId(
      playerId: $playerId
      limitPeriodType: $limitPeriodType
    ) {
      id
      lossLimit {
        dayLimit {
          ...PeriodLimit
        }
        weekLimit {
          ...PeriodLimit
        }
        monthLimit {
          ...PeriodLimit
        }
      }
    }
  }
  ${limitFragment}
`;

const PlayerCancelGlobalLossLimitForm: FC<{
  playerId: string;
  limitPeriodType: LimitTypeEnum;
}> = ({ playerId, limitPeriodType }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerCancelGlobalLossLimitFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerCancelGlobalLossLimitsForm;

  const [playerCancelGlobalLossLimitState, cancelPendingGlobalLossLimit] =
    useMutation<
      CancelPlayerGlobalLossLimitMutation,
      CancelPlayerGlobalLossLimitMutationVariables
    >(cancelPendingGlobalLossLimitMutation);

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = (_values: {}) => {
    if (playerId) {
      cancelPendingGlobalLossLimit({
        playerId,
        limitPeriodType,
      }).then((res) => {
        if (res.error?.message && isMounted) {
          setErrorMessage(res.error?.message);
        } else if (close) {
          close();
        }
      });
    }
  };

  if (!form) {
    return null;
  }
  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={{}}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={playerCancelGlobalLossLimitState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerCancelGlobalLossLimitForm;
